<template>
  <v-dialog :value="mostrar" persistent max-width="560" scrollable>
    <v-card class="as-card">
      <v-card-title class="as-head">
        <v-icon left color="#00695C">mdi-shield-key-outline</v-icon>
        <span class="as-title">Ativar acesso</span>
      </v-card-title>

      <v-divider></v-divider>

      <!-- ETAPA 1: dados de cobrança -->
      <v-card-text v-if="etapa === 'form'" class="as-body">
        <div class="as-free">
          <v-icon left small color="#1E7A5E">mdi-cash-multiple</v-icon>
          <span>Ative sua assinatura mensal. A cobrança é feita automaticamente via <strong>PIX</strong>.</span>
        </div>

        <p class="as-section">Plano</p>
        <v-select
          v-model="form.planoId"
          :items="planos"
          item-text="faixa"
          item-value="id"
          outlined dense color="#00695C" hide-details="auto"
          label="Porte da igreja (faixa de membros)"
          class="mb-2"
        ></v-select>
        <div class="as-valor" v-if="planoSelecionado">
          <span class="as-valor-num">{{ valorLabel }}</span>
          <span class="as-valor-sufixo">/mês</span>
        </div>

        <p class="as-section mt-4">Dados de cobrança</p>
        <v-text-field
          v-model="form.nome" label="Nome completo do responsável*"
          outlined dense color="#00695C" hide-details="auto"
          class="mb-3" :rules="[obrigatorio]"
        ></v-text-field>
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-select v-model="form.tipoDocumento" :items="['CPF', 'CNPJ']" label="Tipo*" outlined dense color="#00695C" hide-details="auto" class="mb-3" @change="form.cpfCnpj = ''"></v-select>
          </v-col>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="form.cpfCnpj" :label="form.tipoDocumento + ' do responsável*'"
              outlined dense color="#00695C" hide-details="auto" type="tel"
              v-mask="form.tipoDocumento === 'CNPJ' ? '##.###.###/####-##' : '###.###.###-##'"
              class="mb-3" :rules="[obrigatorio]"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="7">
            <v-text-field v-model="form.email" label="E-mail do responsável*" outlined dense color="#00695C" hide-details="auto" type="email" class="mb-3" :rules="[obrigatorio]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field v-model="form.telefone" label="Telefone*" outlined dense color="#00695C" hide-details="auto" v-mask="'(##) #####-####'" type="tel" class="mb-3" :rules="[obrigatorio]"></v-text-field>
          </v-col>
        </v-row>

        <v-select
          v-model="form.diaVencimento" :items="diasVencimento"
          label="Dia de vencimento das cobranças*"
          hint="Escolha entre os dias 5, 10, 15, 20, 25 ou 30." persistent-hint
          outlined dense color="#00695C" prepend-inner-icon="mdi-calendar-clock"
          class="mb-3"
        ></v-select>

        <p class="as-section">Endereço de cobrança <span class="as-opcional">(para a nota fiscal)</span></p>
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-text-field v-model="form.endereco.cep" label="CEP*" outlined dense color="#00695C" hide-details="auto" type="tel" v-mask="'#####-###'" :loading="buscandoCep" @blur="buscarCep" class="mb-3"></v-text-field>
          </v-col>
          <v-col cols="12" sm="8">
            <v-text-field v-model="form.endereco.logradouro" label="Logradouro*" outlined dense color="#00695C" hide-details="auto" class="mb-3"></v-text-field>
          </v-col>
          <v-col cols="6" sm="4">
            <v-text-field v-model="form.endereco.numero" label="Número*" outlined dense color="#00695C" hide-details="auto" type="tel" class="mb-3"></v-text-field>
          </v-col>
          <v-col cols="6" sm="8">
            <v-text-field v-model="form.endereco.bairro" label="Bairro" outlined dense color="#00695C" hide-details="auto" class="mb-3"></v-text-field>
          </v-col>
          <v-col cols="8">
            <v-text-field v-model="form.endereco.municipio" label="Cidade*" outlined dense color="#00695C" hide-details="auto" class="mb-3"></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="form.endereco.estado" label="UF*" outlined dense color="#00695C" hide-details="auto" maxlength="2" class="mb-3"></v-text-field>
          </v-col>
        </v-row>

        <v-btn
          block large depressed dark color="#00695C" class="as-btn"
          :loading="ativando" @click="ativar"
        >
          <v-icon left>mdi-rocket-launch</v-icon>Ativar acesso
        </v-btn>
        <button class="as-sair" @click="sair">Sair</button>
      </v-card-text>

      <!-- ETAPA 2: ativado -->
      <v-card-text v-else class="as-body as-center">
        <v-icon size="64" color="#2E9E78">mdi-check-circle</v-icon>
        <p class="as-ok">Acesso ativado!</p>
        <p class="as-intro">
          Sua assinatura está ativa.
          <template v-if="proximaCobranca"> Próxima cobrança em <strong>{{ proximaCobrancaLabel }}</strong>.</template>
        </p>
        <p class="as-intro">Redirecionando...</p>
      </v-card-text>
    </v-card>

    <Snackbar ref="chamaSnackbar" />
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { baseApiUrl } from '@/global'
import { PLANOS, formatBRL, planoPorId } from '@/plans'
import Snackbar from './snackbar.vue'

export default {
  name: 'AssinaturaDialog',
  components: { Snackbar },
  data() {
    return {
      planos: PLANOS,
      // TESTE: bloqueio de ativação desligado (não trava o acesso).
      // Para reativar a obrigatoriedade do pagamento, mude para true.
      bloqueioObrigatorio: false,
      igreja: null,
      igrejaCarregada: false,
      etapa: 'form',
      ativando: false,
      proximaCobranca: '',
      diasVencimento: [5, 10, 15, 20, 25, 30],
      buscandoCep: false,
      form: {
        planoId: '',
        nome: '',
        tipoDocumento: 'CPF',
        cpfCnpj: '',
        email: '',
        telefone: '',
        diaVencimento: null,
        endereco: { cep: '', logradouro: '', numero: '', bairro: '', municipio: '', estado: '' },
      },
      obrigatorio: v => !!v || 'Obrigatório',
    }
  },
  computed: {
    ...mapState({
      logado: state => state.isMenuVisible,
      user: state => state.userObj,
    }),
    // Só exige ativação de contas novas (contrato aceito) cuja igreja
    // está inativa. Contas antigas (contratoAceito undefined) não são afetadas.
    precisaAtivar() {
      return !!this.logado && this.user && this.user.id && this.user.contratoAceito === true
    },
    mostrar() {
      // Só para igrejas criadas via autocontratação (flag própria) e ainda não
      // ativadas. Igrejas antigas (ex.: Liberdade) não têm a flag → nunca mostra.
      return this.bloqueioObrigatorio && this.precisaAtivar && this.igrejaCarregada && this.igreja
        && this.igreja.autocontratacao === true && this.igreja.ativo !== true
    },
    igrejaId() {
      if (this.user && this.user.igrejaId) return this.user.igrejaId
      try { const u = JSON.parse(sessionStorage.getItem('user')); return (u && u.igrejaId) || '' } catch (e) { return '' }
    },
    planoSelecionado() { return planoPorId(this.form.planoId) },
    valorLabel() { return this.planoSelecionado ? formatBRL(this.planoSelecionado.valor) : '—' },
    proximaCobrancaLabel() {
      if (!this.proximaCobranca) return ''
      const d = new Date(this.proximaCobranca)
      return isNaN(d) ? this.proximaCobranca : d.toLocaleDateString('pt-BR')
    },
  },
  watch: {
    precisaAtivar: { immediate: true, handler(v) { if (v && !this.igrejaCarregada) this.carregarIgreja() } },
  },
  methods: {
    async carregarIgreja() {
      if (!this.igrejaId) return
      try {
        const token = sessionStorage.getItem('accToken')
        const res = await axios.get(`${baseApiUrl}Igrejas/${this.igrejaId}?access_token=${token}`)
        this.igreja = res.data || null
        if (this.igreja && this.igreja.planoId) this.form.planoId = this.igreja.planoId
        // Pré-preenche o responsável com os dados conhecidos.
        if (!this.form.nome) this.form.nome = (this.user && this.user.nome) || ''
        if (!this.form.email) this.form.email = (this.user && this.user.email) || (this.igreja && this.igreja.email) || ''
        if (!this.form.telefone) this.form.telefone = (this.igreja && this.igreja.telefone) || (this.user && this.user.telefone) || ''
      } catch (e) {
        this.igreja = null
      } finally {
        this.igrejaCarregada = true
      }
    },
    validar() {
      const f = this.form
      if (!f.planoId) return 'Selecione o plano.'
      if (!f.nome) return 'Informe o nome do responsável.'
      const doc = (f.cpfCnpj || '').replace(/\D/g, '')
      const tam = f.tipoDocumento === 'CNPJ' ? 14 : 11
      if (doc.length !== tam) return 'Informe um ' + f.tipoDocumento + ' válido.'
      if (!/.+@.+\..+/.test(f.email || '')) return 'Informe um e-mail válido.'
      if (!f.telefone) return 'Informe o telefone do responsável.'
      if (!f.diaVencimento) return 'Selecione o dia de vencimento das cobranças.'
      const e = f.endereco
      if (!e.cep || !e.logradouro || !e.numero || !e.municipio || !e.estado)
        return 'Preencha o endereço de cobrança (necessário para a nota fiscal).'
      return null
    },
    async buscarCep() {
      const cep = (this.form.endereco.cep || '').replace(/\D/g, '')
      if (cep.length !== 8) return
      this.buscandoCep = true
      try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)
        if (data && !data.erro) {
          this.form.endereco.logradouro = data.logradouro || this.form.endereco.logradouro
          this.form.endereco.bairro = data.bairro || this.form.endereco.bairro
          this.form.endereco.municipio = data.localidade || this.form.endereco.municipio
          this.form.endereco.estado = data.uf || this.form.endereco.estado
        }
      } catch (e) { /* CEP preenchível manualmente */ } finally {
        this.buscandoCep = false
      }
    },
    async ativar() {
      const msg = this.validar()
      if (msg) { this.$refs.chamaSnackbar.snack('#F9A825', msg, 'ATENÇÃO'); return }
      this.ativando = true
      try {
        const token = sessionStorage.getItem('accToken')
        const { data } = await axios.post(`${baseApiUrl}Pagamentos/criarAssinatura?access_token=${token}`, {
          planoId: this.form.planoId,
          nome: this.form.nome,
          cpfCnpj: this.form.cpfCnpj,
          email: this.form.email,
          telefone: this.form.telefone,
          diaVencimento: this.form.diaVencimento,
          endereco: this.form.endereco,
        })
        this.proximaCobranca = (data && data.proximaCobranca) || ''
        this.concluir()
      } catch (error) {
        const m = (error.response && error.response.data && error.response.data.error && error.response.data.error.message)
          ? error.response.data.error.message
          : 'Não foi possível ativar o acesso. Tente novamente.'
        this.$refs.chamaSnackbar.snack('#EF5350', m, 'ERRO')
      } finally {
        this.ativando = false
      }
    },
    concluir() {
      this.etapa = 'ok'
      if (this.igreja) this.igreja.ativo = true
      setTimeout(() => { window.location.reload() }, 2200)
    },
    sair() {
      this.$store.dispatch('Sair')
      this.$router.push({ path: '/login' })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600&display=swap');

.as-card { border-radius: 16px !important; }
.as-head { padding: 18px 22px; }
.as-title { font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: 1.25rem; color: #16322E; }
.as-body { max-height: 64vh; }
.as-center { text-align: center; }
.as-intro { color: #5b6b67; margin: 10px 0; }
.as-free { display: flex; align-items: flex-start; gap: 6px; background: #E9F6F0; border: 1px solid #cfe9df; border-radius: 12px; padding: 12px 14px; color: #1E7A5E; font-size: .9rem; margin: 16px 0 18px; }
.as-section { font-size: .74rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #00695C; margin: 6px 0 10px; }
.as-opcional { font-weight: 500; text-transform: none; letter-spacing: 0; color: #97a3a0; }
.as-valor { display: flex; align-items: baseline; gap: 6px; }
.as-valor-num { font-family: 'Fraunces', Georgia, serif; font-weight: 700; font-size: 1.5rem; color: #00695C; }
.as-valor-sufixo { color: #5b6b67; font-size: .9rem; }
.as-btn { border-radius: 999px !important; text-transform: none !important; letter-spacing: 0 !important; margin-top: 14px; }
.as-ok { font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: 1.3rem; color: #16322E; margin: 10px 0 4px; }
.as-sair { display: block; margin: 14px auto 0; background: none; border: none; cursor: pointer; color: #97a3a0; font-size: .85rem; text-decoration: underline; }
</style>
